import type { TemplateInput, FooterLink as ServiceFooterLink, FooterSocialLink as ServiceFooterSocialLink, NavLink as ServiceNavLink } from '@amzn/mulberry-internal-typescript-client';
import type { HeaderProps, FooterProps, TopPromoBannerProps, FooterLink, FooterSocialLink, NavBarLink } from '@amzn/burberry-ui-components';
import { parseArrayField } from 'src/utils/parseField';
import type { SiteManifest } from 'src/utils/siteUtils';
import { CUSTOM_PAGE_MANIFEST_KEY } from 'src/utils/siteUtils';

const FOOTER_LINK_TYPE_MAP: Record<string, 'tos' | 'pp' | 'faq'> = {
  TERMS_OF_SERVICE: 'tos',
  PRIVACY_POLICY: 'pp',
  FREQUENTLY_ASKED_QUESTIONS: 'faq',
};

const SOCIAL_PLATFORM_MAP: Record<string, FooterSocialLink['platform']> = {
  INSTAGRAM: 'instagram',
  TIKTOK: 'tiktok',
  X: 'x',
  FACEBOOK: 'facebook',
  YOUTUBE: 'youtube',
};

const mapNavBarLinks = (links?: ServiceNavLink[] | string): NavBarLink[] | undefined => {
  const parsed = parseArrayField<ServiceNavLink>(links);
  if (!parsed) return undefined;
  return parsed
    .filter((link) => link.linkText && link.linkUrl)
    .map((link) => ({
      linkText: link.linkText!,
      linkUrl: link.linkUrl!,
    }));
};

const FOOTER_LINK_ROUTE_MAP: Record<string, string> = {
  tos: '/terms',
  pp: '/privacy-policy',
  faq: '/faq',
};

const mapFooterLinks = (links?: ServiceFooterLink[] | string, manifest?: SiteManifest | null): FooterLink[] | undefined => {
  const parsed = parseArrayField<ServiceFooterLink>(links) ?? [];

  // Build a lookup of existing footer link entries by their normalized type
  const linkByType: Record<string, ServiceFooterLink> = {};
  parsed.forEach((link) => {
    if (link.type && FOOTER_LINK_TYPE_MAP[link.type]) {
      const type = FOOTER_LINK_TYPE_MAP[link.type];
      linkByType[type] = link;
    }
  });

  // Iterate over all known page types — if manifest has a custom page for a type,
  // include it even if no footer link entry exists in the template config.
  const result: FooterLink[] = [];
  for (const [, type] of Object.entries(FOOTER_LINK_TYPE_MAP)) {
    const link = linkByType[type];
    const hasCustomPage = !!manifest?.[CUSTOM_PAGE_MANIFEST_KEY[type]];

    if (link?.url && link.useCustomPage !== true) {
      // External url takes precedence over custom page per spec.
      // Only an explicit useCustomPage === true opts into the internal page; null/undefined/false with a url all resolve to the external url.
      result.push({ type, url: link.url, text: link.text ?? '' });
    } else if (hasCustomPage || link?.useCustomPage === true) {
      // Live: manifest has custom page in S3 → route to internal page
      // Preview: manifest not fetched, use useCustomPage flag from DDB instead
      result.push({ type, url: FOOTER_LINK_ROUTE_MAP[type], text: link?.text ?? '' });
    } else if (link) {
      // No custom page, no external url — fall back to internal route
      result.push({ type, url: FOOTER_LINK_ROUTE_MAP[type], text: link.text ?? '' });
    }
  }

  return result.length > 0 ? result : undefined;
};

const mapFooterSocialLinks = (links?: ServiceFooterSocialLink[] | string): FooterSocialLink[] | undefined => {
  const parsed = parseArrayField<ServiceFooterSocialLink>(links);
  if (!parsed) return undefined;
  return parsed
    .filter((link) => link.platform && SOCIAL_PLATFORM_MAP[link.platform])
    .map((link) => ({
      platform: SOCIAL_PLATFORM_MAP[link.platform!],
      url: link.url ?? '',
    }));
};

const getHomeLink = (): string => {
  const params = new URLSearchParams(window.location.search);
  const preserved = new URLSearchParams();
  const keys = ['site', 'preview', 'version', 'asinStage'];
  keys.forEach((key) => {
    const val = params.get(key);
    if (val) preserved.set(key, val);
  });
  const qs = preserved.toString();
  return qs ? `/?${qs}` : '/';
};

export const getHeaderProps = (template: TemplateInput): Omit<HeaderProps, 'metricEmitter'> => ({
  navBarPrimaryText: template.navBarPrimaryText || '',
  navBarMainImage: template.navBarMainImage,
  navBarMainImageWidth: template.navBarMainImageWidth,
  navBarMainLink: template.navBarMainLink || getHomeLink(),
  navBarLeftImage: template.navBarLeftImage,
  navBarLeftImageWidth: template.navBarLeftImageWidth,
  navBarLeftLink: template.navBarLeftLink,
  navBarBackgroundImage: template.navBarBackgroundImage,
  navBarHeight: template.navBarHeight,
  navBarLinks: mapNavBarLinks(template.navBarLinks),
  navBarBackToSiteUrl: template.navBarBackToSiteUrl,
  navBarBackToSiteText: template.navBarBackToSiteText,
});

export const getFooterProps = (template: TemplateInput, manifest?: SiteManifest | null): Omit<FooterProps, 'metricEmitter'> => ({
  footerPrimaryText: template.footerPrimaryText,
  footerSocialLinks: mapFooterSocialLinks(template.footerSocialLinks),
  footerLinks: mapFooterLinks(template.footerLinks, manifest),
  showAccessibilitySupport: template.showAccessibilitySupport,
  footerBanner: template.footerBanner,
});

export const getTopPromoBannerProps = (template: TemplateInput): Omit<TopPromoBannerProps, 'metricEmitter'> => ({
  topPromoBannerText: template.topPromoBannerText,
  topPromoBannerBackgroundColor: template.topPromoBannerBackgroundColor,
  topPromoBannerBackgroundImage: template.topPromoBannerBackgroundImage,
  topPromoBannerHeight: template.topPromoBannerHeight,
});
